/**
 * Session 3a — shared API client for the 17 bespoke pages built in
 * PRs 3-19.
 *
 * As of Session 3a-BACKEND (PRs 1-5), every endpoint listed below is
 * live and per-tenant scoped against the FastAPI backend:
 *
 *   PR 1: /api/people/{new-joiners,leavers}
 *         (probation watch lives at /api/contracts/probation-ending-soon
 *          since M12.6 — ProbationWatchPage uses `api.getProbationEndingSoon`)
 *   PR 2: /api/leave/{adjustments,encashment,carry-forward/preview,
 *         carry-forward/run,inquiries,bulk-actions}
 *   PR 3: /api/loans/{disbursement-queue,active,settlements,arrears,
 *         article-90-watch}
 *   PR 4: /api/excuses/{history,quota-watch}
 *   PR 5: /api/ai/{decisions,scanner-health,reasoning}
 *
 * The 404/501 fallback in `_safeGet` is retained as defense-in-depth
 * for new tenants that haven't been migrated forward yet — production
 * Riyadh hits live endpoints + the seed script
 * (`scripts.seed_riyadh_session_3a_fixtures`) ensures every page shows
 * real demo data.
 */

import { apiBaseUrl, ApiError, authHeaders } from "./api";

async function _safeGet<T>(path: string, fallback: T): Promise<T> {
  // Carry the Clerk JWT + tenant-slug headers that the FastAPI tenant
  // middleware requires. Without these we 401 in production. The
  // 404/501 fallback is retained as defense-in-depth for new tenants
  // that haven't been migrated forward to the Session 3a-BACKEND
  // endpoints.
  let headers: Record<string, string>;
  try {
    headers = await authHeaders();
  } catch {
    headers = {};
  }
  headers["Content-Type"] = "application/json";

  try {
    const r = await fetch(`${apiBaseUrl}${path}`, { headers });
    if (!r.ok) {
      if (r.status === 404 || r.status === 501) return fallback;
      let detail = r.statusText;
      try {
        const body = await r.json();
        if (body && typeof body.detail === "string") detail = body.detail;
      } catch {
        // Non-JSON error body — keep statusText.
      }
      throw new ApiError(r.status, detail);
    }
    return (await r.json()) as T;
  } catch (e) {
    if (e instanceof ApiError) throw e;
    // Network / connection refused — fall back gracefully so the
    // page still renders.
    return fallback;
  }
}

// ---------------------------------------------------------------------------
// People watch-lists (PR 3-4)
// ---------------------------------------------------------------------------

export interface NewJoinerItem {
  employee_id: string;
  employee_code: string;
  employee_first_name_en: string;
  employee_last_name_en: string;
  hire_date: string;
  days_since_hire: number;
  position_title: string | null;
  department_name: string | null;
  branch_name: string | null;
  onboarding_progress_pct: number;
  missing_documents: string[];
  probation_days_total: number;
  probation_days_elapsed: number;
  buddy_name: string | null;
}

export interface LeaverItem {
  employee_id: string;
  employee_code: string;
  employee_first_name_en: string;
  employee_last_name_en: string;
  termination_date: string;
  days_since_termination: number;
  reason_code: string;
  reason_label: string;
  eos_state: string;
  settlement_amount_sar: number | null;
  clearance_items_complete: number;
  clearance_items_total: number;
  gov_filings: {
    qiwa: "sent" | "pending" | "na";
    gosi: "sent" | "pending" | "na";
    iqama: "sent" | "pending" | "na";
  };
}

// ---------------------------------------------------------------------------
// Leave second-line (PR 5-9)
// ---------------------------------------------------------------------------

export interface LeaveAdjustmentItem {
  event_id: string;
  employee_id: string;
  employee_code: string;
  employee_name_en: string;
  leave_type_code: string;
  leave_type_name_en: string;
  delta_days: number;
  reason: string;
  hr_actor_name: string;
  hr_actor_id: string | null;
  adjusted_at: string;
  source: "hr_initiated" | "concierge" | "bulk_import";
}

export interface EncashmentRunItem {
  run_id: string;
  initiated_by_name: string;
  initiated_at: string;
  status: "draft" | "calculated" | "approved" | "paid";
  employees_count: number;
  total_days: number;
  total_amount_sar: number;
  last_action_label: string;
}

export interface CarryForwardPreviewItem {
  employee_id: string;
  employee_code: string;
  employee_name_en: string;
  leave_type_code: string;
  leave_type_name_en: string;
  current_balance_days: number;
  carry_forward_cap_days: number;
  to_carry_forward_days: number;
  to_expire_days: number;
  recommended: "carry_forward" | "encash" | "expire";
  recommended_rationale: string;
}

export interface CarryForwardSummary {
  leave_year_end: string;
  total_employees_affected: number;
  total_days_at_risk: number;
  total_to_carry_forward: number;
  total_to_expire: number;
  total_encashment_cost_sar: number;
  items: CarryForwardPreviewItem[];
}

export interface LeaveInquiryItem {
  request_id: string;
  employee_id: string;
  employee_name_en: string;
  leave_type_code: string;
  leave_type_name_en: string;
  category: string;
  start_date: string;
  end_date: string;
  days: number;
  status: string;
  approver_name: string | null;
}

// ---------------------------------------------------------------------------
// Loans second-line (PR 10-14)
// ---------------------------------------------------------------------------

export interface LoanDisbursementItem {
  application_id: string;
  employee_id: string;
  employee_name_en: string;
  product_code: string;
  product_name_en: string;
  approved_amount_sar: number;
  tenure_months: number;
  approved_at: string;
  days_waiting: number;
  approver_name: string | null;
}

export interface ActiveLoanItem {
  application_id: string;
  employee_id: string;
  employee_name_en: string;
  product_code: string;
  product_name_en: string;
  outstanding_sar: number;
  monthly_installment_sar: number;
  months_remaining: number;
  article_90_headroom_pct: number;
  last_payment_status: "on_time" | "late" | "skipped";
  last_payment_date: string | null;
  consecutive_missed: number;
  status: "in_repayment" | "arrears" | "in_dispute";
}

export interface SettlementItem {
  settlement_id: string;
  application_id: string;
  employee_id: string;
  employee_name_en: string;
  outstanding_at_request_sar: number;
  proposed_settlement_sar: number;
  request_date: string;
  status: "pending_approval" | "approved" | "paid" | "denied";
  approver_name: string | null;
}

export interface ArrearsItem {
  application_id: string;
  employee_id: string;
  employee_name_en: string;
  outstanding_sar: number;
  consecutive_missed: number;
  last_successful_payment_date: string | null;
  months_since_last_payment: number;
  total_missed_amount_sar: number;
  status: "normal" | "arrears_escalated" | "in_dispute" | "written_off";
  escalation_date: string | null;
}

export interface Article90WatchItem {
  employee_id: string;
  employee_code: string;
  employee_name_en: string;
  monthly_wage_sar: number;
  current_deductions_sar: number;
  article_90_cap_sar: number;
  headroom_sar: number;
  headroom_pct: number;
  status: "safe" | "attention" | "critical" | "over_cap";
  last_mutabaa_scan_at: string | null;
}

// ---------------------------------------------------------------------------
// Excuses second-line (PR 15-16)
// ---------------------------------------------------------------------------

export interface ExcuseHistoryItem {
  excuse_id: string;
  employee_id: string;
  employee_name_en: string;
  date: string;
  type_code: string;
  type_label_en: string;
  category: "late" | "early" | "permission" | "sick" | "personal";
  duration_minutes: number;
  reason: string;
  status: "approved" | "denied" | "cancelled";
  approver_name: string | null;
  monthly_used: number;
  monthly_limit: number;
}

export interface QuotaWatchItem {
  employee_id: string;
  employee_code: string;
  employee_name_en: string;
  excuse_type_code: string;
  excuse_type_label_en: string;
  used_this_month: number;
  monthly_limit: number;
  monthly_utilization_pct: number;
  used_this_year: number;
  yearly_limit: number;
  yearly_utilization_pct: number;
  status: "safe" | "attention" | "critical" | "exceeded";
}

// ---------------------------------------------------------------------------
// AI module (PR 17-19)
// ---------------------------------------------------------------------------

export interface DecisionRegistryItem {
  decision_id: string;
  owning_agent: "tashghil" | "rawatib" | "mutabaa" | "khidmat";
  decision_type: "DET" | "AI" | "AI_DET_HYBRID";
  context_bundle_class: string;
  mode_shape: "DEFAULT" | "ALWAYS_HUMAN_FIRST" | "ALWAYS_AUTONOMOUS";
  confidence_thresholds: {
    conservative: number;
    standard: number;
    aggressive: number;
  };
  registered_at: string;
  source_module: string;
  last_used_at: string | null;
  invocation_count: number;
}

export interface ScannerHealthItem {
  scan_name: string;
  owning_agent: "mutabaa";
  interval_label_en: string;
  last_run_at: string | null;
  next_run_at: string | null;
  last_status: "success" | "failed" | "running" | "paused";
  findings_24h: number;
  consecutive_failures: number;
  status: "healthy" | "degraded" | "paused";
}

export interface ReasoningTraceItem {
  trace_id: string;
  emitted_at: string;
  agent: "tashghil" | "rawatib" | "mutabaa" | "khidmat";
  decision_id: string;
  entity_type: string;
  entity_id: string;
  goal_pursued: string;
  plan_step_count: number;
  tool_call_count: number;
  outcome: "success" | "failed" | "escalated" | "pending";
  confidence: number;
  mode_at_decision: "Conservative" | "Standard" | "Aggressive";
  hash_chain_verified: boolean;
}

// ---------------------------------------------------------------------------
// API stubs — graceful 404 fallbacks for the 17 surfaces.
// ---------------------------------------------------------------------------

export const session3aApi = {
  listNewJoiners: (legalEntityId: string, days = 30) =>
    _safeGet<{ items: NewJoinerItem[]; as_of: string }>(
      `/api/people/new-joiners?legal_entity_id=${encodeURIComponent(legalEntityId)}&days=${days}`,
      { items: [], as_of: new Date().toISOString() },
    ),

  listLeavers: (legalEntityId: string, days = 30) =>
    _safeGet<{ items: LeaverItem[]; as_of: string }>(
      `/api/people/leavers?legal_entity_id=${encodeURIComponent(legalEntityId)}&days=${days}`,
      { items: [], as_of: new Date().toISOString() },
    ),

  listLeaveAdjustments: (legalEntityId: string, days = 30) =>
    _safeGet<{ items: LeaveAdjustmentItem[] }>(
      `/api/leave/adjustments?legal_entity_id=${encodeURIComponent(legalEntityId)}&days=${days}`,
      { items: [] },
    ),

  listEncashmentRuns: (legalEntityId: string) =>
    _safeGet<{ items: EncashmentRunItem[] }>(
      `/api/leave/encashment?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),

  previewCarryForward: (legalEntityId: string, leaveYearEnd: string) =>
    _safeGet<CarryForwardSummary>(
      `/api/leave/carry-forward/preview?legal_entity_id=${encodeURIComponent(legalEntityId)}&leave_year_end=${leaveYearEnd}`,
      {
        leave_year_end: leaveYearEnd,
        total_employees_affected: 0,
        total_days_at_risk: 0,
        total_to_carry_forward: 0,
        total_to_expire: 0,
        total_encashment_cost_sar: 0,
        items: [],
      },
    ),

  searchLeaveInquiries: (legalEntityId: string, query: string) =>
    _safeGet<{ items: LeaveInquiryItem[]; understood_filters: Record<string, string> }>(
      `/api/leave/inquiries?legal_entity_id=${encodeURIComponent(legalEntityId)}&q=${encodeURIComponent(query)}`,
      { items: [], understood_filters: {} },
    ),

  listLoanDisbursementQueue: (legalEntityId: string) =>
    _safeGet<{ items: LoanDisbursementItem[] }>(
      `/api/loans/disbursement-queue?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),

  listActiveLoans: (legalEntityId: string) =>
    _safeGet<{ items: ActiveLoanItem[] }>(
      `/api/loans/active?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),

  listSettlements: (legalEntityId: string, status?: string) =>
    _safeGet<{ items: SettlementItem[] }>(
      `/api/loans/settlements?legal_entity_id=${encodeURIComponent(legalEntityId)}${status ? `&status=${status}` : ""}`,
      { items: [] },
    ),

  listArrears: (legalEntityId: string) =>
    _safeGet<{ items: ArrearsItem[] }>(
      `/api/loans/arrears?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),

  listArticle90Watch: (legalEntityId: string) =>
    _safeGet<{ items: Article90WatchItem[] }>(
      `/api/loans/article-90-watch?legal_entity_id=${encodeURIComponent(legalEntityId)}`,
      { items: [] },
    ),

  listExcuseHistory: (legalEntityId: string, employeeId?: string, days = 90) =>
    _safeGet<{ items: ExcuseHistoryItem[] }>(
      `/api/excuses/history?legal_entity_id=${encodeURIComponent(legalEntityId)}&days=${days}${employeeId ? `&employee_id=${encodeURIComponent(employeeId)}` : ""}`,
      { items: [] },
    ),

  listQuotaWatch: (legalEntityId: string, thresholdPct = 80) =>
    _safeGet<{ items: QuotaWatchItem[] }>(
      `/api/excuses/quota-watch?legal_entity_id=${encodeURIComponent(legalEntityId)}&threshold_pct=${thresholdPct}`,
      { items: [] },
    ),

  listDecisionRegistry: (filters: { owning_agent?: string; mode_shape?: string } = {}) => {
    const qs = new URLSearchParams();
    if (filters.owning_agent) qs.append("owning_agent", filters.owning_agent);
    if (filters.mode_shape) qs.append("mode_shape", filters.mode_shape);
    const q = qs.toString();
    return _safeGet<{ items: DecisionRegistryItem[] }>(
      `/api/ai/decisions${q ? `?${q}` : ""}`,
      { items: [] },
    );
  },

  listScannerHealth: () =>
    _safeGet<{
      items: ScannerHealthItem[];
      kpis: { healthy: number; degraded: number; paused: number; total: number };
    }>(
      `/api/ai/scanner-health`,
      { items: [], kpis: { healthy: 0, degraded: 0, paused: 0, total: 0 } },
    ),

  listReasoningTraces: (filters: { agent?: string; entity_id?: string; days?: number } = {}) => {
    const qs = new URLSearchParams();
    if (filters.agent) qs.append("agent", filters.agent);
    if (filters.entity_id) qs.append("entity_id", filters.entity_id);
    if (filters.days) qs.append("days", String(filters.days));
    const q = qs.toString();
    return _safeGet<{ items: ReasoningTraceItem[]; chain_intact: boolean }>(
      `/api/ai/reasoning${q ? `?${q}` : ""}`,
      { items: [], chain_intact: true },
    );
  },
};
